import React from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { media, layout, remCalc } from '@prisjakt/ui-base';
import { withViewport } from '@prisjakt/ui-viewport';
import { H3Text, TitleMediumText } from '@prisjakt/ui/text/variants';

export const ProductPage = styled.div.attrs({ 'data-test': 'ProductPage' })`
  position: relative;

  /* This is needed or the review action dropdown will be obscured */
  .pj-ui-panel--row {
    overflow: visible;
  }

  .pj-ui-scroll-link {
    button:hover {
      color: ${p => p.theme.text.callToActionHover};
      text-decoration: underline;
    }
  }

  .pj-ui-price-row {
    width: 100%;
  }

  .product-header--content {
    flex-direction: row;
  }
`;

export const CategoryRank = styled.div`
  font-size: 14px;
  margin-top: 12px;
  color: ${p => p.theme.colors.base600};

  a {
    color: ${p => p.theme.colors.maybeBlue700};
    cursor: pointer;
    outline: none;

    &:hover,
    &:focus {
      color: ${p => p.theme.colors.maybeBlue500};
      text-decoration: underline;
    }
  }
`;

const backToTopButtonDropShadow = '0 1px 3px rgba(0, 0, 0, 0.3)';

type BackToTopButtonProps = {
  viewport: {
    top: number;
    scroll: {
      yDirection: number;
    };
  };
};

const BackToTopButtonComponent = styled.button.attrs(() => ({
  type: 'button',
}))<BackToTopButtonProps>`
  position: fixed;
  bottom: 10px;
  ${media.hideOutsiderAd`
    right: 10px;
  `};
  ${media.showOutsiderAd`
    left: 50%;
    margin-left: ${layout.siteWidth.value * 0.5 - 200}px;
  `};
  background: ${p => p.theme.colors.yellove500};
  border-radius: ${layout.borderRadius};
  box-shadow: ${backToTopButtonDropShadow};
  border: 1px solid ${p => p.theme.colors.yellove500};
  cursor: pointer;
  padding: 10px 12px;
  visibility: ${props =>
    props.viewport &&
    props.viewport.top > 500 &&
    props.viewport.scroll.yDirection > 0
      ? 'visible'
      : 'hidden'};
  opacity: ${props =>
    props.viewport &&
    props.viewport.top > 500 &&
    props.viewport.scroll.yDirection > 0
      ? 0.9
      : 0};
  transition: opacity 0.2s linear;
  z-index: 4;

  :hover {
    background-color: ${p => p.theme.colors.yellove500};
  }

  :focus {
    background-color: ${p => p.theme.colors.yellove500};
    box-shadow: ${backToTopButtonDropShadow}, 0 0 0 4px #000;
    opacity: 1;
  }

  :focus:not(:focus-visible) {
    box-shadow: ${backToTopButtonDropShadow};
  }
`;

export const BackToTopButton = withViewport(BackToTopButtonComponent);

export const Rating = styled.div`
  cursor: pointer;
  &:hover,
  &:focus {
    text-decoration: underline;
  }
`;

export const WriteReview = styled.div`
  cursor: pointer;
  color: ${p => p.theme.text.linkExternal};

  &:hover,
  &:focus {
    color: ${p => p.theme.text.linkExternalHover};
    text-decoration: underline;
  }
`;

type LinkedTabProps = {
  active: boolean;
  to: string;
};

const LinkedTab = ({ active, ...props }: LinkedTabProps) => {
  return <Link {...props} />;
};

const tabTitleLineHeight = layout.lineHeight;
const tabTitleFontSize = { desktop: 14, mobile: 10 };

export const TabTitle = styled.div`
  flex: 0 0 100%;
  font-weight: 600;
  font-size: ${remCalc(tabTitleFontSize.mobile)};
  line-height: ${tabTitleLineHeight}em;
  opacity: 0.7;
  transition: opacity 0.1s linear;

  ${media.largeUp`
    font-size: ${remCalc(tabTitleFontSize.desktop)};
  `};
`;

const highlightedTabTitleStyle = css`
  opacity: 1;
`;

const tabSubtitleLineHeight = 1.2;
const tabSubtitleFontSize = 10;
const tabSubtitleHeight = tabSubtitleFontSize * tabSubtitleLineHeight * 2;

export const TabSubtitle = styled.div`
  font-size: ${remCalc(tabSubtitleFontSize)};
  flex: 0 0 100%;
  height: ${remCalc(tabSubtitleHeight)};
  line-height: ${tabSubtitleLineHeight}em;
  opacity: 0.5;
  transition: all 0.1s linear;

  display: flex;
  justify-content: center;
  align-items: center;
`;

const highlightedTabSubtitleStyle = css`
  opacity: 1;
`;

export const TabLowestPrice = styled(TabSubtitle)`
  font-size: 12px;
  font-weight: 600;
`;

const highlightedTabLowestPriceStyle = css`
  color: ${p => p.theme.colors.negative700};
`;

export const TabRating = styled(TabSubtitle)`
  filter: grayscale(1);
`;

const highlightedTabRatingStyle = css`
  filter: none;
`;

export const TabSparkline = styled(TabSubtitle)`
  filter: grayscale(1);
`;

const highlightedTabSparklineStyle = css`
  filter: none;
`;

export const Tab = styled(LinkedTab)<LinkedTabProps>`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: flex-start;
  flex: 1 1 auto;
  padding: 6px 10px;
  background: ${props =>
    props.active ? props.theme.colors.base0 : 'transparent'};
  border-radius: 5px;
  border-bottom: 0 none;
  text-align: center;
  color: ${props => props.theme.colors.base700};
  cursor: ${props => (props.active ? 'default' : 'pointer')};
  hyphens: auto;

  :hover,
  :focus {
    ${TabTitle} {
      ${highlightedTabTitleStyle}
    }
    ${TabSubtitle} {
      ${highlightedTabSubtitleStyle}
    }
    ${TabLowestPrice} {
      ${highlightedTabLowestPriceStyle}
    }
    ${TabRating} {
      ${highlightedTabRatingStyle}
    }
    ${TabSparkline} {
      ${highlightedTabSparklineStyle}
    }
  }

  ${props =>
    props.active &&
    css`
      ${TabTitle} {
        ${highlightedTabTitleStyle}
      }
      ${TabSubtitle} {
        ${highlightedTabSubtitleStyle}
      }
      ${TabLowestPrice} {
        ${highlightedTabLowestPriceStyle}
      }
      ${TabRating} {
        ${highlightedTabRatingStyle}
      }
      ${TabSparkline} {
        ${highlightedTabSparklineStyle}
      }
    `}

  ${media.largeUp`
    flex: 1 0 0;
  `};
`;

export const TabContent = styled.div``;

type TabContentWrapperProps = {
  spacingTop: boolean;
};

export const TabContentWrapper = styled.div<TabContentWrapperProps>`
  margin-top: ${p => (p.spacingTop ? remCalc(16) : 0)};
  padding: 0 ${layout.pageContent.gutter.rem};
`;

export const BreadCrumbWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

type HCProps = { visible?: boolean };
export const HideableContent = styled.div<HCProps>`
  display: ${props => (props.visible ? 'block' : 'none')};
`;

export const ReviewsWrapper = styled.section`
  display: flex;
  flex-direction: row;

  @supports (display: grid) {
    display: grid;
    grid-template-columns: 70% 30%;
    column-gap: ${layout.measurements.spacingSmall};
  }

  ${media.mediumDown`
    @supports (display: grid) {
      grid-template-columns: 1fr;
      row-gap: ${layout.measurements.spacingMedium};
    }
    
    flex-direction: column;
  `};
`;

export const Reviews = styled.main`
  padding: 0 ${layout.measurements.spacingSmall};
  width: 70%;
  order: 0;

  @supports (display: grid) {
    width: auto;
  }

  ${media.mediumDown`
    width: 100%;
    order: 1;
  `};
`;

export const ReviewsSidebar = styled.aside`
  padding: 0 ${layout.measurements.spacingSmall};
  width: 30%;
  order: 1;

  @supports (display: grid) {
    width: auto;
  }

  ${media.mediumDown`
    width: 100%;
    order: 0;
  `};
`;

export const ExpertContent = styled.div`
  margin-top: ${layout.measurements.spacingMedium};
`;

export const Title = styled(H3Text)`
  display: block;
  font-weight: 700;
  margin-bottom: ${layout.measurements.spacingNormal};
`;

export const Subtitle = styled(TitleMediumText)`
  display: block;
  font-weight: 700;
  margin-bottom: ${layout.measurements.spacingNormal};
`;
